// Canonical audit event shape for the `/audit/events` boundary. This is the
// typed trust boundary for the product's activity feed: malformed rows drop
// rather than render. The record IS resource history — notifications and
// messages are separate records with their own lifecycles.
export type ZoriAuditActor = {
  id: string;
  displayName: string;
  email: string | null;
  username: string | null;
};

export type ZoriAuditEvent = {
  id: string;
  organizationId: string;
  actorId: string | null;
  /** Server-joined actor summary — null when the actor row is absent. */
  actor: ZoriAuditActor | null;
  /** Impersonation/effective-actor id when recorded. */
  effectiveActorId: string | null;
  /** Impersonation/effective-actor display name when recorded. */
  effectiveDisplayName: string | null;
  action: string;
  target: string | null;
  outcome: string | null;
  correlationId: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
};

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function str(row: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

function normalizeActor(value: unknown): ZoriAuditActor | null {
  const row = record(value);
  if (!row) return null;
  const id = str(row, "id");
  if (!id) return null;
  return {
    id,
    displayName: str(row, "displayName", "display_name") ?? "Unknown User",
    email: str(row, "email"),
    username: str(row, "username"),
  };
}

export function normalizeAuditEvent(value: unknown): ZoriAuditEvent | null {
  const row = record(value);
  if (!row) return null;
  const id = str(row, "id");
  const action = str(row, "action");
  if (!id || !action) return null;
  const effective = record(row.effectiveActor) ?? record(row.effective_actor);
  return {
    id,
    organizationId: str(row, "organizationId", "organization_id") ?? "",
    actorId: str(row, "actorId", "actor_id"),
    actor: normalizeActor(row.actor),
    effectiveActorId:
      str(row, "effectiveActorId", "effective_actor_id") ??
      (effective ? str(effective, "id") : null),
    effectiveDisplayName: effective
      ? str(effective, "displayName", "display_name")
      : null,
    action,
    target: str(row, "target"),
    outcome: str(row, "outcome"),
    correlationId: str(row, "correlationId", "correlation_id"),
    payload: record(row.payload),
    createdAt: str(row, "createdAt", "created_at") ?? "",
  };
}

export function normalizeAuditEvents(value: unknown): ZoriAuditEvent[] {
  const rows = Array.isArray(value)
    ? value
    : (record(value)?.data as unknown) ?? (record(value)?.rows as unknown);
  if (!Array.isArray(rows)) return [];
  const events: ZoriAuditEvent[] = [];
  for (const row of rows) {
    const event = normalizeAuditEvent(row);
    if (event) events.push(event);
  }
  return events;
}
